if (!customElements.get('product-modal')) {
  customElements.define(
    'product-modal',
    class ProductModal extends ModalDialog {
      constructor() {
        super();
        this.zoom = 1;
        this.panX = 0;
        this.panY = 0;
        this.dragging = false;
        this.onThumbClick = this.onThumbClick.bind(this);
        this.onPrev = this.onPrev.bind(this);
        this.onNext = this.onNext.bind(this);
        this.onKeyNav = this.onKeyNav.bind(this);
        this.onZoomIn = this.onZoomIn.bind(this);
        this.onZoomOut = this.onZoomOut.bind(this);
        this.onWheelZoom = this.onWheelZoom.bind(this);
        this.onPointerDown = this.onPointerDown.bind(this);
        this.onPointerMove = this.onPointerMove.bind(this);
        this.onPointerUp = this.onPointerUp.bind(this);
        this.onContentClick = this.onContentClick.bind(this);
        this.querySelector('.meroo-media-modal__panel')?.addEventListener('pointerup', (event) => {
          event.stopPropagation();
        });
      }

      connectedCallback() {
        super.connectedCallback();
        this.thumbs = [...this.querySelectorAll('[data-meroo-modal-thumb]')];
        this.prevBtn = this.querySelector('[data-meroo-modal-prev]');
        this.nextBtn = this.querySelector('[data-meroo-modal-next]');
        this.zoomInBtn = this.querySelector('[data-meroo-modal-zoom-in]');
        this.zoomOutBtn = this.querySelector('[data-meroo-modal-zoom-out]');
        this.content = this.querySelector('[data-meroo-modal-content]');

        this.thumbs.forEach((thumb) => thumb.addEventListener('click', this.onThumbClick));
        this.prevBtn?.addEventListener('click', this.onPrev);
        this.nextBtn?.addEventListener('click', this.onNext);
        this.zoomInBtn?.addEventListener('click', this.onZoomIn);
        this.zoomOutBtn?.addEventListener('click', this.onZoomOut);
        this.content?.addEventListener('wheel', this.onWheelZoom, { passive: false });
        this.content?.addEventListener('pointerdown', this.onPointerDown);
        this.content?.addEventListener('pointermove', this.onPointerMove);
        this.content?.addEventListener('pointerup', this.onPointerUp);
        this.content?.addEventListener('pointercancel', this.onPointerUp);
        this.content?.addEventListener('pointerleave', this.onPointerUp);
        this.content?.addEventListener('click', this.onContentClick);
      }

      hide() {
        this.removeEventListener('keydown', this.onKeyNav);
        this.endDrag();
        this.setZoom(1);
        super.hide();
      }

      show(opener) {
        super.show(opener);
        this.addEventListener('keydown', this.onKeyNav);
        this.setZoom(1);
        this.showActiveMedia();
      }

      getMediaItems() {
        return [...this.querySelectorAll('[data-meroo-modal-content] [data-media-id]')];
      }

      activeMedia() {
        return this.querySelector('[data-meroo-modal-content] .active');
      }

      activeIndex() {
        const items = this.getMediaItems();
        return Math.max(
          0,
          items.findIndex((item) => item.classList.contains('active'))
        );
      }

      applyTransform() {
        const active = this.activeMedia();
        if (!active) return;
        if (this.zoom <= 1) {
          active.style.transform = '';
          this.panX = 0;
          this.panY = 0;
        } else {
          active.style.transform = `translate3d(${this.panX}px, ${this.panY}px, 0) scale(${this.zoom})`;
        }
        active.classList.toggle('is-zoomed', this.zoom > 1);
        if (this.content) {
          this.content.classList.toggle('is-zoomed', this.zoom > 1);
          this.content.classList.toggle('is-dragging', this.dragging);
        }
      }

      clampPan() {
        if (this.zoom <= 1 || !this.content) {
          this.panX = 0;
          this.panY = 0;
          return;
        }
        const bounds = this.content.getBoundingClientRect();
        // Keep some of the zoomed image on-screen while dragging.
        const maxX = (bounds.width * (this.zoom - 1)) / 2;
        const maxY = (bounds.height * (this.zoom - 1)) / 2;
        this.panX = Math.max(-maxX, Math.min(maxX, this.panX));
        this.panY = Math.max(-maxY, Math.min(maxY, this.panY));
      }

      setZoom(next, { resetPan = false } = {}) {
        const previous = this.zoom;
        this.zoom = Math.min(3, Math.max(1, Math.round((Number(next) || 1) * 100) / 100));
        if (this.zoom === 1 || resetPan || this.zoom < previous) {
          // Reset pan when fully zoomed out; gently clamp when zooming out partially.
          if (this.zoom === 1) {
            this.panX = 0;
            this.panY = 0;
          } else {
            this.clampPan();
          }
        }
        this.applyTransform();
        if (this.zoomOutBtn) this.zoomOutBtn.disabled = this.zoom <= 1;
        if (this.zoomInBtn) this.zoomInBtn.disabled = this.zoom >= 3;
      }

      onZoomIn(event) {
        event.preventDefault();
        event.stopPropagation();
        this.setZoom(this.zoom + 0.5);
      }

      onZoomOut(event) {
        event.preventDefault();
        event.stopPropagation();
        this.setZoom(this.zoom - 0.5);
      }

      onWheelZoom(event) {
        if (!event.ctrlKey && !event.metaKey) {
          if (this.zoom > 1) {
            event.preventDefault();
            this.panX -= event.deltaX;
            this.panY -= event.deltaY;
            this.clampPan();
            this.applyTransform();
          }
          return;
        }
        event.preventDefault();
        this.setZoom(this.zoom + (event.deltaY < 0 ? 0.25 : -0.25));
      }

      onPointerDown(event) {
        if (this.zoom <= 1) return;
        if (event.button != null && event.button !== 0) return;
        if (event.target.closest('button, a, input, textarea, select')) return;
        this.dragging = true;
        this.dragMoved = false;
        this.dragStartX = event.clientX;
        this.dragStartY = event.clientY;
        this.panStartX = this.panX;
        this.panStartY = this.panY;
        this.content?.setPointerCapture?.(event.pointerId);
        this.applyTransform();
        event.preventDefault();
      }

      onPointerMove(event) {
        if (!this.dragging || this.zoom <= 1) return;
        const dx = event.clientX - this.dragStartX;
        const dy = event.clientY - this.dragStartY;
        if (Math.abs(dx) > 2 || Math.abs(dy) > 2) this.dragMoved = true;
        this.panX = this.panStartX + dx;
        this.panY = this.panStartY + dy;
        this.clampPan();
        this.applyTransform();
        event.preventDefault();
      }

      onPointerUp(event) {
        if (!this.dragging) return;
        this.endDrag(event.pointerId);
      }

      endDrag(pointerId) {
        if (pointerId != null) {
          try {
            this.content?.releasePointerCapture?.(pointerId);
          } catch (_) {
            /* already released */
          }
        }
        this.dragging = false;
        this.applyTransform();
      }

      onContentClick(event) {
        if (event.target.closest('button, a, input, textarea, select')) return;
        // Ignore the click that ends a drag so pan doesn't also toggle zoom.
        if (this.dragMoved) {
          this.dragMoved = false;
          event.preventDefault();
          event.stopPropagation();
          return;
        }
        if (this.zoom > 1) {
          this.setZoom(1, { resetPan: true });
        } else {
          this.setZoom(2);
        }
      }

      setActiveById(mediaId) {
        if (!mediaId) return;
        const items = this.getMediaItems();
        items.forEach((element) => {
          const match = String(element.getAttribute('data-media-id')) === String(mediaId);
          element.classList.toggle('active', match);
          element.style.transform = '';
          element.classList.remove('is-zoomed');
          if (match) {
            const activeMediaTemplate = element.querySelector('template');
            const activeMediaContent = activeMediaTemplate ? activeMediaTemplate.content : null;
            if (element.nodeName === 'DEFERRED-MEDIA' && activeMediaContent?.querySelector('.js-youtube')) {
              element.loadContent();
            }
          }
        });

        this.thumbs.forEach((thumb) => {
          const match = String(thumb.dataset.mediaId) === String(mediaId);
          thumb.classList.toggle('is-active', match);
          if (match) {
            thumb.setAttribute('aria-current', 'true');
            thumb.scrollIntoView({ block: 'nearest', inline: 'nearest' });
          } else {
            thumb.removeAttribute('aria-current');
          }
        });

        const multi = items.length > 1;
        if (this.prevBtn) this.prevBtn.hidden = !multi;
        if (this.nextBtn) this.nextBtn.hidden = !multi;
        this.endDrag();
        this.setZoom(1, { resetPan: true });
      }

      showActiveMedia() {
        const mediaId = this.openedBy?.getAttribute('data-media-id');
        const items = this.getMediaItems();
        const fallback = items[0]?.getAttribute('data-media-id');
        this.setActiveById(mediaId || fallback);
      }

      onThumbClick(event) {
        event.preventDefault();
        event.stopPropagation();
        this.setActiveById(event.currentTarget.dataset.mediaId);
      }

      step(delta) {
        const items = this.getMediaItems();
        if (items.length < 2) return;
        const next = (this.activeIndex() + delta + items.length) % items.length;
        this.setActiveById(items[next].getAttribute('data-media-id'));
      }

      onPrev(event) {
        event.preventDefault();
        event.stopPropagation();
        this.step(-1);
      }

      onNext(event) {
        event.preventDefault();
        event.stopPropagation();
        this.step(1);
      }

      onKeyNav(event) {
        if (event.key === 'ArrowLeft') {
          event.preventDefault();
          this.step(-1);
        } else if (event.key === 'ArrowRight') {
          event.preventDefault();
          this.step(1);
        } else if (event.key === '+' || event.key === '=') {
          event.preventDefault();
          this.setZoom(this.zoom + 0.5);
        } else if (event.key === '-' || event.key === '_') {
          event.preventDefault();
          this.setZoom(this.zoom - 0.5);
        } else if (event.key === 'Escape' && this.zoom > 1) {
          event.preventDefault();
          this.setZoom(1, { resetPan: true });
        }
      }
    }
  );
}
